/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, { Fragment, useState } from 'react'
import { Combobox, ComboboxInput, ComboboxLabel, ComboboxOptions, Transition } from '@headlessui/react'
import { useDebouncedCallback } from 'use-debounce'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import {
    AddressLookupResult,
    loadGoogleMaps,
    parseAddressFromDescription,
    resolvePlaceAddress,
    searchGoogleAddresses,
} from 'services/googleAddressService'
import UiIcon from 'components/Ui/UiIcon'
import UiLoader from 'components/Ui/UiLoader'
import UiLocationSearchOptionsList from './UiLocationSearchComponents/UiLocationSearchOptionsList'

interface UiLocationSearchProps {
    name?: string
    label?: string
    placeholder?: string
    helperText?: string
    onSelect: (address: AddressLookupResult) => void
    countryCode?: string
    defaultValue?: string
    value?: string
    onChange?: (value: string) => void
    className?: string
    error?: string
    required?: boolean
    /** Clear the search query after a suggestion is selected (default: true). */
    clearOnSelect?: boolean
}

const UiLocationSearch: React.FC<UiLocationSearchProps> = ({
    name,
    label,
    placeholder,
    helperText,
    onSelect,
    countryCode = 'CH',
    defaultValue = '',
    value,
    onChange,
    className,
    error,
    required,
    clearOnSelect = true,
}) => {
    const { t, i18n } = useTranslation()
    const [googleApiLoaded, setGoogleApiLoaded] = useState(false)
    const [googleApiBlocked, setGoogleApiBlocked] = useState(false)
    const [internalQuery, setInternalQuery] = useState(defaultValue)
    const [results, setResults] = useState<Awaited<ReturnType<typeof searchGoogleAddresses>>>([])
    const [isLoading, setIsLoading] = useState(false)

    React.useEffect(() => {
        let isMounted = true

        loadGoogleMaps(i18n.resolvedLanguage || i18n.language)
            .then(() => {
                if (isMounted) {
                    setGoogleApiLoaded(true)
                    setGoogleApiBlocked(false)
                }
            })
            .catch((loadError) => {
                console.error('Failed to load Google Maps:', loadError)
                if (isMounted) {
                    setGoogleApiBlocked(true)
                }
            })

        return () => {
            isMounted = false
        }
    }, [i18n.language, i18n.resolvedLanguage])

    const handleSearch = useDebouncedCallback(async (val: string) => {
        if (!val || val.length < 2) {
            setResults([])
            return
        }

        setIsLoading(true)
        try {
            const data = await searchGoogleAddresses(val, countryCode, i18n.resolvedLanguage || i18n.language)
            setResults(data)
        } catch (err) {
            console.error('Failed to search location:', err)
            setResults([])
        } finally {
            setIsLoading(false)
        }
    }, 400)

    const actualQuery = value !== undefined ? value : internalQuery

    const setQuery = (val: string) => {
        if (onChange) onChange(val)
        setInternalQuery(val)
    }

    const handleInputChange = (val: string) => {
        setQuery(val)
        handleSearch(val)
    }

    const resetSearch = () => {
        setQuery('')
        setResults([])
    }

    return (
        <div className={clsx('UiLocationSearch w-full', className)}>
            <Combobox
                value={actualQuery}
                onChange={async (item: (typeof results)[number] | string | null) => {
                    if (!item || typeof item === 'string') {
                        if (typeof item === 'string') {
                            handleInputChange(item)
                        }
                        return
                    }

                    setIsLoading(true)
                    try {
                        let address: AddressLookupResult
                        try {
                            address = await resolvePlaceAddress(
                                item.placeId,
                                item.description,
                                i18n.resolvedLanguage || i18n.language
                            )
                        } catch (resolveError) {
                            console.warn('Place resolve failed, using suggestion text:', resolveError)
                            address = parseAddressFromDescription(item.description)
                        }
                        onSelect(address)
                        if (clearOnSelect) {
                            resetSearch()
                        } else {
                            setQuery(item.description)
                            setResults([])
                        }
                    } catch (selectError) {
                        console.error('Failed to apply selected location:', selectError)
                        onSelect(parseAddressFromDescription(item.description))
                        if (clearOnSelect) {
                            resetSearch()
                        }
                    } finally {
                        setIsLoading(false)
                    }
                }}
                nullable
            >
                {label && (
                    <ComboboxLabel
                        data-testid={name ? 'label-' + name : undefined}
                        className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
                    >
                        {label}
                        {required && <span className="text-red-500 ml-1">*</span>}
                    </ComboboxLabel>
                )}
                <div className="relative mt-1">
                    <div
                        className={clsx(
                            'relative w-full cursor-default overflow-hidden rounded-lg bg-white dark:bg-gray-900 text-left border focus-within:ring-2 focus-within:ring-indigo-500 dark:focus-within:ring-indigo-400 focus-within:border-transparent sm:text-sm',
                            error ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-700'
                        )}
                    >
                        {!googleApiLoaded && (
                            <div className="absolute inset-y-0 right-8 flex items-center">
                                <UiLoader size="xs" className="w-4 h-4 text-gray-400" />
                            </div>
                        )}
                        <ComboboxInput
                            data-testid={name ? 'input-' + name : undefined}
                            className={clsx(
                                'w-full border-none py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 dark:text-gray-100 bg-transparent focus:border-0 focus:outline-none',
                                error && 'text-red-900 placeholder-red-300'
                            )}
                            onChange={(event) => {
                                handleInputChange(event.target.value)
                            }}
                            displayValue={() => actualQuery}
                            placeholder={placeholder || t('Search address...')}
                            autoComplete="off"
                            disabled={!googleApiLoaded}
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                            {isLoading ? (
                                <UiLoader size="xs" className="w-4 h-4 text-gray-400" />
                            ) : (
                                <UiIcon name="search" className="w-4 h-4 text-gray-400" />
                            )}
                        </div>
                    </div>

                    {error && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>}
                    {!error && googleApiBlocked && (
                        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                            {t('Address suggestions unavailable. You can still type the address manually.')}
                        </p>
                    )}
                    {!error && !googleApiBlocked && helperText && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{helperText}</p>
                    )}

                    <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <ComboboxOptions className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-800 py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                            <UiLocationSearchOptionsList
                                results={results}
                                query={actualQuery}
                                isLoading={isLoading}
                                translate={t}
                            />
                        </ComboboxOptions>
                    </Transition>
                </div>
            </Combobox>
        </div>
    )
}

export default UiLocationSearch
